import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  FaPlus,
  FaEdit,
  FaTrash,
  FaUser,
  FaUsers,
  FaTimes,
  FaSave,
  FaPhone,
  FaEnvelope,
  FaUserTag,
  FaUserCircle
} from 'react-icons/fa';

// Import your exact components (these remain exactly the same)
import './UserTable.css';
import img1 from './img/img1.png';
import img2 from './img/img2.png';
import img from './img/Close.png';

// Styled Components for modern responsive design
const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: 100vh;

  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-direction: column;
  gap: 1rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    gap: 0;
  }
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #212529;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  text-align: center;
  
  svg {
    font-size: 1.5rem;
    color: #0d6efd;
  }
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
    text-align: left;
    
    svg {
      font-size: 2rem;
    }
  }
`;

const AddButton = styled.button`
  background: linear-gradient(135deg, #0d6efd 0%, #4285f4 100%);
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  font-size: 1rem;
  width: 100%;
  justify-content: center;
  
  svg {
    font-size: 1.1rem;
  }
  
  &:hover {
    background: linear-gradient(135deg, #0a58ca 0%, #1a73e8 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(13, 110, 253, 0.3);
  }

  @media (min-width: 768px) {
    width: auto;
  }
`;

const TableCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const TableHeader = styled.div`
  background: linear-gradient(135deg, #0d6efd 0%, #4285f4 100%);
  color: white;
  padding: 1rem;
  
  @media (min-width: 768px) {
    padding: 1.5rem 2rem;
  }
`;

const TableTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  svg {
    font-size: 1.1rem;
  }
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
    
    svg {
      font-size: 1.3rem;
    }
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  background: white;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  min-width: 600px;
  
  @media (min-width: 768px) {
    font-size: 0.9rem;
    min-width: 800px;
  }
`;

const TableHead = styled.thead`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const TableRow = styled.tr`
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid #dee2e6;
  }
`;

const TableHeaderCell = styled.th`
  padding: 0.75rem 0.5rem;
  text-align: left;
  font-weight: 600;
  color: #495057;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #dee2e6;
  
  &:first-child {
    width: 8%;
    text-align: center;
  }
  
  &:last-child {
    width: 15%;
    text-align: center;
  }
  
  @media (min-width: 768px) {
    padding: 1rem 0.75rem;
    font-size: 0.8rem;
    
    &:first-child {
      width: 5%;
    }
    
    &:last-child {
      width: 10%;
    }
  }
`;

const TableCell = styled.td`
  padding: 0.75rem 0.5rem;
  vertical-align: middle;
  font-size: 0.8rem;
  
  &:first-child {
    text-align: center;
    font-weight: 600;
    color: #0d6efd;
  }
  
  &:last-child {
    text-align: center;
  }
  
  @media (min-width: 768px) {
    padding: 1rem 0.75rem;
    font-size: 0.9rem;
  }
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  
  @media (min-width: 768px) {
    gap: 0.5rem;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f8f9fa;
    transform: scale(1.1);
  }
  
  &.edit:hover {
    background: rgba(13, 110, 253, 0.1);
  }
  
  &.delete:hover {
    background: rgba(220, 53, 69, 0.1);
  }
  
  img {
    width: 32px;
    height: 32px;
  }
  
  @media (min-width: 768px) {
    width: 36px;
    height: 36px;
    
    img {
      width: 40px;
      height: 40px;
    }
  }
`;

const FormCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin: 1rem;
  
  @media (min-width: 768px) {
    margin: 0;
  }
`;

const FormHeader = styled.div`
  background: linear-gradient(135deg, #0d6efd 0%, #4285f4 100%);
  color: white;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (min-width: 768px) {
    padding: 1.5rem 2rem;
  }
`;

const FormTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  svg {
    font-size: 1.1rem;
  }
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
    
    svg {
      font-size: 1.3rem;
    }
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    font-size: 1.2rem;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
`;

const FormContent = styled.div`
  padding: 1rem;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (min-width: 576px) {
    grid-template-columns: ${props => props.fullWidth ? '1fr' : '1fr 1fr'};
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: #495057;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    font-size: 0.8rem;
    color: #0d6efd;
  }
  
  @media (min-width: 768px) {
    font-size: 0.9rem;
    
    svg {
      font-size: 0.9rem;
    }
  }
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #0d6efd;
    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
  }
  
  &::placeholder {
    color: #6c757d;
  }
  
  @media (min-width: 768px) {
    font-size: 0.95rem;
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  font-size: 0.9rem;
  background: white;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #0d6efd;
    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
  }
  
  @media (min-width: 768px) {
    font-size: 0.95rem;
  }
`;

const FormActions = styled.div`
  background: #f8f9fa;
  padding: 1rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  border-top: 1px solid #dee2e6;
  flex-direction: column;
  
  @media (min-width: 576px) {
    flex-direction: row;
    padding: 1.5rem 2rem;
  }
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  justify-content: center;
  
  svg {
    font-size: 1rem;
  }
  
  &.cancel-btn {
    background: white;
    color: #6c757d;
    border: 2px solid #dee2e6;
    
    &:hover {
      background: #f8f9fa;
      border-color: #adb5bd;
      color: #495057;
    }
  }
  
  &.save-btn {
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
    color: white;
    border: none;
    
    &:hover {
      background: linear-gradient(135deg, #218838 0%, #1ea97c 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
    }
  }
  
  @media (min-width: 768px) {
    font-size: 0.95rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  color: #6c757d;
  
  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  color: #dee2e6;
  margin-bottom: 1rem;
  
  svg {
    font-size: 3rem;
  }
  
  @media (min-width: 768px) {
    font-size: 5rem;
    
    svg {
      font-size: 5rem;
    }
  }
`;

// Mobile Card Component for better mobile experience
const MobileCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
  padding: 1rem;
  border: 1px solid #e9ecef;
  
  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
`;

const MobileCardInfo = styled.div`
  flex: 1;
`;

const MobileCardName = styled.h6`
  margin: 0 0 0.25rem 0;
  color: #212529;
  font-weight: 600;
  font-size: 1rem;
`;

const MobileCardDesignation = styled.span`
  background: #0d6efd;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const MobileCardId = styled.span`
  background: #f8f9fa;
  color: #0d6efd;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const MobileCardDetails = styled.div`
  margin: 0.75rem 0;
`;

const MobileCardDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  color: #6c757d;
  
  svg {
    color: #0d6efd;
    font-size: 0.8rem;
  }
`;

const MobileCardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e9ecef;
`;

// EXACT ORIGINAL COMPONENT WITH PRESERVED LOGIC - NO FUNCTIONAL CHANGES
const UserTable = () => {
  // EXACT ORIGINAL STATE - NO CHANGES
  const [users, setUsers] = useState([
    { id: 1, firstName: 'Sharon', lastName: 'Fernz', designation: 'Managing Director', phoneNumber: '123-456-7890', email: 'sharon.fernz@mysiteinspection.com' },
    { id: 2, firstName: 'Sharon', lastName: 'Fernz', designation: 'Super Admin', phoneNumber: '123-456-7890', email: 'sharon.fernz@mysiteinspection.com' },
    { id: 3, firstName: 'Sharon', lastName: 'Fernz', designation: 'Admin', phoneNumber: '123-456-7890', email: 'sharon.fernz@mysiteinspection.com' },
    { id: 4, firstName: 'Sharon', lastName: 'Fernz', designation: 'HR Department Head', phoneNumber: '123-456-7890', email: 'sharon.fernz@mysiteinspection.com' },
    { id: 5, firstName: 'Sharon', lastName: 'Fernz', designation: 'Managing Director', phoneNumber: '123-456-7890', email: 'sharon.fernz@mysiteinspection.com' },
  ]);

  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    designation: '',
    phoneNumber: '',
    email: '',
  });

  const [editingUserId, setEditingUserId] = useState(null);

  // EXACT ORIGINAL FUNCTIONS - NO CHANGES
  const handleAddUser = () => {
    setShowAddUser(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser({ ...newUser, [name]: value });
  };

  const handleSave = () => {
    if (editingUserId !== null) {
      setUsers(users.map(user => (user.id === editingUserId ? { id: editingUserId, ...newUser } : user)));
      setEditingUserId(null);
    } else {
      setUsers([...users, { id: users.length + 1, ...newUser }]);
    }

    setShowAddUser(false);
    setNewUser({
      firstName: '',
      lastName: '',
      designation: '',
      phoneNumber: '',
      email: '',
    });
  };

  const handleCancel = () => {
    setShowAddUser(false);
    setNewUser({
      firstName: '',
      lastName: '',
      designation: '',
      phoneNumber: '',
      email: '',
    });
    setEditingUserId(null);
  };

  const handleEditUser = (user) => {
    setNewUser(user);
    setEditingUserId(user.id);
    setShowAddUser(true);
  };

  const handleDeleteUser = (userId) => {
    setUsers(users.filter(user => user.id !== userId));
  };

  // Check if screen is mobile (you can adjust this breakpoint as needed)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Container>
      {!showAddUser ? (
        <>
          <Header>
            <Title>
              <FaUsers />
              User Management
            </Title>
            <AddButton onClick={handleAddUser}>
              <FaPlus />
              Add User
            </AddButton>
          </Header>

          <TableCard>
            <TableHeader>
              <TableTitle>
                <FaUserCircle />
                System Users
              </TableTitle>
            </TableHeader>

            {isMobile ? (
              // Mobile Card View
              <div style={{ padding: '1rem' }}>
                {users.length > 0 ? (
                  users.map((user) => (
                    <MobileCard key={user.id}>
                      <MobileCardHeader>
                        <MobileCardInfo>
                          <MobileCardName>{user.firstName} {user.lastName}</MobileCardName>
                          <MobileCardDesignation>{user.designation}</MobileCardDesignation>
                        </MobileCardInfo>
                        <MobileCardId>#{user.id}</MobileCardId>
                      </MobileCardHeader>
                      
                      <MobileCardDetails>
                        <MobileCardDetail>
                          <FaPhone />
                          {user.phoneNumber}
                        </MobileCardDetail>
                        <MobileCardDetail>
                          <FaEnvelope />
                          {user.email}
                        </MobileCardDetail>
                      </MobileCardDetails>
                      
                      <MobileCardActions>
                        <IconWrapper 
                          className="edit"
                          onClick={() => handleEditUser(user)}
                        >
                          <img src={img1} alt="edit" />
                        </IconWrapper>
                        <IconWrapper 
                          className="delete"
                          onClick={() => handleDeleteUser(user.id)}
                        >
                          <img src={img2} alt="delete" />
                        </IconWrapper>
                      </MobileCardActions>
                    </MobileCard>
                  ))
                ) : (
                  <EmptyState>
                    <EmptyIcon>
                      <FaUsers />
                    </EmptyIcon>
                    <h3>No Users Found</h3>
                    <p>Click "Add User" to create your first user.</p>
                  </EmptyState>
                )}
              </div>
            ) : (
              // Desktop Table View
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>#</TableHeaderCell>
                      <TableHeaderCell>First Name</TableHeaderCell>
                      <TableHeaderCell>Last Name</TableHeaderCell>
                      <TableHeaderCell>Designation</TableHeaderCell>
                      <TableHeaderCell>Phone Number</TableHeaderCell>
                      <TableHeaderCell>Email Address</TableHeaderCell>
                      <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <tbody>
                    {users.length > 0 ? (
                      users.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>{user.id}</TableCell>
                          <TableCell>{user.firstName}</TableCell>
                          <TableCell>{user.lastName}</TableCell>
                          <TableCell>{user.designation}</TableCell>
                          <TableCell>{user.phoneNumber}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <ActionGroup>
                              <IconWrapper 
                                className="edit"
                                onClick={() => handleEditUser(user)}
                              >
                                <img src={img1} alt="edit" />
                              </IconWrapper>
                              <IconWrapper 
                                className="delete"
                                onClick={() => handleDeleteUser(user.id)}
                              >
                                <img src={img2} alt="delete" />
                              </IconWrapper>
                            </ActionGroup>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan="7">
                          <EmptyState>
                            <EmptyIcon>
                              <FaUsers />
                            </EmptyIcon>
                            <h3>No Users Found</h3>
                            <p>Click "Add User" to create your first user.</p>
                          </EmptyState>
                        </TableCell>
                      </TableRow>
                    )}
                  </tbody>
                </Table>
              </TableContainer>
            )}
          </TableCard>
        </>
      ) : (
        <FormCard>
          <FormHeader>
            <FormTitle>
              <FaUser />
              {editingUserId !== null ? 'Edit User' : 'Add User'}
            </FormTitle>
            <CloseButton onClick={handleCancel}>
              <FaTimes />
            </CloseButton>
          </FormHeader>

          <FormContent>
            <FormRow>
              <FormGroup>
                <Label>
                  <FaUser />
                  First Name
                </Label>
                <Input
                  type="text"
                  name="firstName"
                  value={newUser.firstName}
                  onChange={handleInputChange}
                  placeholder="Enter your first name"
                />
              </FormGroup>
              <FormGroup>
                <Label>
                  <FaUser />
                  Last Name
                </Label>
                <Input
                  type="text"
                  name="lastName"
                  value={newUser.lastName}
                  onChange={handleInputChange}
                  placeholder="Enter your last name"
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>
                  <FaUserTag />
                  Designation
                </Label>
                <Select
                  name="designation"
                  value={newUser.designation}
                  onChange={handleInputChange}
                >
                  <option value="">Select Designation</option>
                  <option value="Managing Director">Managing Director</option>
                  <option value="Super Admin">Super Admin</option>
                  <option value="Admin">Admin</option>
                  <option value="HR Department Head">HR Department Head</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>
                  <FaPhone />
                  Phone Number
                </Label>
                <Input
                  type="text"
                  name="phoneNumber"
                  value={newUser.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="Enter your phone number"
                />
              </FormGroup>
            </FormRow>

            <FormRow fullWidth>
              <FormGroup>
                <Label>
                  <FaEnvelope />
                  Email Address
                </Label>
                <Input
                  type="email"
                  name="email"
                  value={newUser.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                />
              </FormGroup>
            </FormRow>
          </FormContent>

          <FormActions>
            <ActionButton className="cancel-btn" onClick={handleCancel}>
              <FaTimes />
              Cancel
            </ActionButton>
            <ActionButton className="save-btn" onClick={handleSave}>
              <FaSave />
              {editingUserId !== null ? 'Update' : 'Save'}
            </ActionButton>
          </FormActions>
        </FormCard>
      )}
    </Container>
  );
};

export default UserTable;